import { LucideIcon, Package, Search } from "lucide-react";
import { TradeItem, TradeListType } from "../model/tradeList";
import { TradeCard } from "modules/swap-calculator/model/tradeCard";
import { getCardPrice } from "utils/utils";
import { GameId } from "modules/common/model/models";
import {
  finishSpec,
  priceForFinish,
  primaryDisplayFinish,
} from "modules/cards/model/finishBehavior";
import { NONFOIL_FINISH } from "modules/cards/model/card";

export enum TradeRelationshipType {
  THEY_WANT = "they_want",
  THEY_HAVE = "they_have",
}

export interface TradeRelationship {
  type: TradeRelationshipType;
  icon: LucideIcon;
  text: string;
  color: string;
  bgColor: string;
}

export interface CategorizedMatches {
  youWantFromThem: TradeItem[]; // They have, you want
  theyWantFromYou: TradeItem[]; // You have, they want
}

/**
 * Determines the trade relationship between a trade item and the current user
 * Note: In matched trades context, only THEY_WANT and THEY_HAVE relationships appear
 */
export function getTradeRelationship(
  item: TradeItem,
  currentUserId: string
): TradeRelationship {
  const isUserItem = item.user?.id === currentUserId;
  const isWantsList = item.list?.type === TradeListType.WANTS;

  // In matched trades, we only see other users' items, not our own
  // But keeping the logic complete for potential other use cases
  if (!isUserItem) {
    // Other user's item - this is what we see in matched trades
    if (isWantsList) {
      return {
        type: TradeRelationshipType.THEY_WANT,
        icon: Search,
        text: "They want",
        color: "text-secondary",
        bgColor: "bg-secondary/10",
      };
    } else {
      return {
        type: TradeRelationshipType.THEY_HAVE,
        icon: Package,
        text: "They have",
        color: "text-warning",
        bgColor: "bg-warning/10",
      };
    }
  }

  // Fallback for edge cases (shouldn't happen in matched trades)
  if (isWantsList) {
    return {
      type: TradeRelationshipType.THEY_WANT,
      icon: Search,
      text: "They want",
      color: "text-secondary",
      bgColor: "bg-secondary/10",
    };
  } else {
    return {
      type: TradeRelationshipType.THEY_HAVE,
      icon: Package,
      text: "They have",
      color: "text-warning",
      bgColor: "bg-warning/10",
    };
  }
}

/**
 * Categorizes matched trades into logical groups for calculator usage
 */
export function categorizeMatchedTrades(
  matches: TradeItem[],
  currentUserId: string
): CategorizedMatches {
  const categorized: CategorizedMatches = {
    youWantFromThem: [],
    theyWantFromYou: [],
  };

  matches.forEach((item) => {
    const relationship = getTradeRelationship(item, currentUserId);

    // For matched trades, we only have two relationship types:
    // - THEY_WANT: Cards they want (from their wants list)
    // - THEY_HAVE: Cards they have (from their trades list)

    switch (relationship.type) {
      case TradeRelationshipType.THEY_WANT:
        // This is their wants list - you want to trade to them
        categorized.theyWantFromYou.push(item);
        break;
      case TradeRelationshipType.THEY_HAVE:
        // This is their trades list - you want these cards from them
        categorized.youWantFromThem.push(item);
        break;
    }
  });

  return categorized;
}

/**
 * Transforms a TradeItem to a TradeCard for use in the swap calculator
 */
export function transformTradeItemToSwapCard(item: TradeItem): TradeCard {
  const finish = primaryDisplayFinish(item.finishes) ?? NONFOIL_FINISH;
  const price = {
    default: item.price.value.default,
    nonfoil: item.price.value.nonfoil,
    foil: item.price.value.foil,
    metal: item.price.value.metal,
    metalDeluxe: item.price.value.metalDeluxe,
    etched: item.price.value.etched,
    currency: item.price.currency,
  };
  const priceKey = finishSpec(finish).priceKey;
  if (priceKey) price[priceKey] = item.price.value.default;
  const numericPrice = priceForFinish(price, finish);

  return {
    name: item.name,
    number: item.number || "",
    price,
    setCode: item.setCode || "",
    id: item.dataId,
    finish,
    finishes: item.finishes,
    quantity: parseInt(item.quantity, 10) || 1,
    costPrinted: item.costPrinted || "",
    customPrice: numericPrice,
    game: "" as GameId,
  };
}

/**
 * Calculates the total value of a list of trade items
 */
export function calculateTradeItemsTotal(items: TradeItem[]): number {
  return items.reduce((total, item) => {
    const price = getCardPrice(item);
    const numericPrice = parseFloat(price?.replace(/[€$,]/g, "") || "0");
    const quantity = parseInt(item.quantity, 10) || 1;
    return total + numericPrice * quantity;
  }, 0);
}

/**
 * Creates suggested trade combinations to balance values
 */
export function suggestBalancedTrades(
  youWant: TradeItem[],
  theyWant: TradeItem[],
  targetDifference: number = 5 // five-unit tolerance in the active currency
): { yourCards: TradeItem[]; theirCards: TradeItem[]; difference: number }[] {
  const suggestions: {
    yourCards: TradeItem[];
    theirCards: TradeItem[];
    difference: number;
  }[] = [];

  // Simple implementation - can be enhanced with more sophisticated algorithms
  const yourTotal = calculateTradeItemsTotal(theyWant);
  const theirTotal = calculateTradeItemsTotal(youWant);
  const difference = Math.abs(yourTotal - theirTotal);

  if (difference <= targetDifference) {
    suggestions.push({
      yourCards: theyWant,
      theirCards: youWant,
      difference,
    });
  }

  return suggestions;
}
